export type NovaKind = 'invoice' | 'estimate';

export type NovaLine = {
  description: string;
  quantity: number;
  unit_price: number;
};

export type NovaHit = { id: string; label: string };

export type NovaSession = {
  creation: boolean;
  kind: NovaKind | null;
  documentId: string | null;
  polarId: string | null;
  dossiers: NovaHit[];
  clients: NovaHit[];
  clientDossierId: string | null;
};

export type DraftCall = {
  kind: NovaKind;
  creating: boolean;
  dossierId: string | null;
  contactId: string;
  title: string;
  notes: string;
  vatRate: number;
  dueDays: number;
  lines: NovaLine[];
};

export function emptyNovaSession(): NovaSession {
  return {
    creation: false,
    kind: null,
    documentId: null,
    polarId: null,
    dossiers: [],
    clients: [],
    clientDossierId: null,
  };
}

export function polarKind(kind: NovaKind): 'quote' | 'invoice' {
  return kind === 'estimate' ? 'quote' : 'invoice';
}

export function stateCard(session: NovaSession): string {
  const mode = session.creation
    ? `Mode création : ${session.kind === 'estimate' ? 'devis' : 'facture'}`
    : 'Hors mode création';
  const doc = session.documentId ? `Brouillon CIME : ${session.documentId}` : 'Aucun brouillon CIME';
  const polar = session.polarId ? `Brouillon Polaris : ${session.polarId}` : 'Aucun brouillon Polaris';
  const dossiers = session.dossiers.map((d) => `${d.id} ${d.label}`).join(' | ') || 'aucun';
  const clients = session.clients.map((c) => `${c.id} ${c.label}`).join(' | ') || 'aucun';
  return [mode, doc, polar, `Dossiers vus : ${dossiers}`, `Clients vus : ${clients}`].join('\n');
}

function asKind(value: unknown): NovaKind | null {
  if (value === 'invoice') return 'invoice';
  if (value === 'quote' || value === 'estimate') return 'estimate';
  return null;
}

function hit(list: NovaHit[], id: string): boolean {
  return list.some((row) => row.id === id);
}

export function parseLines(raw: unknown): NovaLine[] {
  if (!Array.isArray(raw)) return [];
  const lines: NovaLine[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const description = String(rec.description ?? rec.designation ?? '').trim();
    if (!description) continue;
    const quantity = Number(rec.quantity ?? 1);
    const unit = Number(rec.unit_price_euros ?? rec.unit_price ?? rec.unit_ht_euros ?? 0);
    lines.push({
      description,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      unit_price: Number.isFinite(unit) ? unit : 0,
    });
  }
  return lines;
}

export function guardDraft(
  session: NovaSession,
  name: string,
  args: Record<string, unknown>,
): { ok: true; call: DraftCall } | { ok: false; error: string } {
  const creating = name === 'create_quote_draft' || name === 'create_invoice_draft';
  const saving = name === 'save_quote_draft' || name === 'save_invoice_draft';
  if (!creating && !saving) return { ok: false, error: 'Outil inconnu.' };
  const kind: NovaKind = name.includes('invoice') ? 'invoice' : 'estimate';
  if (!session.creation || session.kind !== kind) {
    return {
      ok: false,
      error: `Ouvre d'abord le mode création ${kind === 'invoice' ? 'facture' : 'devis'} (enter_creation_mode).`,
    };
  }
  if (saving && !session.documentId) {
    return { ok: false, error: 'Aucun brouillon courant. Appelle create_quote_draft ou create_invoice_draft.' };
  }
  const contactId = String(args.contact_id ?? '').trim();
  if (creating && !contactId) return { ok: false, error: 'contact_id manquant.' };
  if (contactId && !hit(session.clients, contactId)) {
    return { ok: false, error: 'contact_id hors du dernier search_clients. Cherche le client du dossier.' };
  }
  const dossierId = String(args.dossier_id ?? session.clientDossierId ?? '').trim();
  if (creating && (!dossierId || !hit(session.dossiers, dossierId))) {
    return { ok: false, error: 'dossier_id hors du dernier search_dossiers.' };
  }
  if (creating && session.clientDossierId && dossierId !== session.clientDossierId) {
    return { ok: false, error: 'Ce client n’appartient pas au dossier choisi. Relance search_clients.' };
  }
  const vat = Number(args.vat_rate ?? 20);
  const due = Number(args.due_days ?? 0);
  return {
    ok: true,
    call: {
      kind,
      creating,
      dossierId: dossierId || null,
      contactId,
      title: String(args.title ?? '').trim(),
      notes: String(args.notes ?? '').trim(),
      vatRate: Number.isFinite(vat) ? vat : 20,
      dueDays: Number.isFinite(due) ? Math.max(0, Math.round(due)) : 0,
      lines: parseLines(args.lines),
    },
  };
}

export function applyNavigation(
  session: NovaSession,
  name: string,
  args: Record<string, unknown>,
): { session: NovaSession; error?: string } {
  if (name === 'enter_creation_mode') {
    const kind = asKind(args.kind) ?? 'invoice';
    const same = session.creation && session.kind === kind;
    return {
      session: same
        ? session
        : {
            ...emptyNovaSession(),
            creation: true,
            kind,
          },
    };
  }
  if (name === 'exit_creation_mode') {
    return { session: emptyNovaSession() };
  }
  return { session, error: 'Outil de navigation inconnu.' };
}
